/**
 * Phase 6 UAT bugfix — register the storm sprite atlas with MapLibre.
 *
 * Background: the map's symbol layer references icon-image keys like
 * `manhole-green` / `catchbasin-yellow` (8 keys total) that live in
 * /sprites/storm@2x.{png,json}. The OFM Liberty style does NOT include this
 * atlas, so MapLibre fires `styleimagemissing` on every key it cannot resolve
 * and the icon-image expression returns nothing — pins show as labels-only.
 *
 * Fix: install a `styleimagemissing` listener that lazily extracts each
 * requested sprite from the atlas manifest and calls `map.addImage`. Atlas
 * is fetched + decoded once and cached for all subsequent extractions.
 *
 * Per CLAUDE.md hard constraint: pin rendering must use a single symbol
 * layer with sprite icons — this fix preserves that architecture (no DOM
 * markers, no per-pin elements).
 */
import type { Map as MaplibreMap } from "maplibre-gl";
import { SPRITE_PATH } from "./style";

interface SpriteRegion {
  x: number;
  y: number;
  width: number;
  height: number;
  pixelRatio?: number;
}

type SpriteManifest = Record<string, SpriteRegion>;

interface LoadedAtlas {
  manifest: SpriteManifest;
  bitmap: ImageBitmap;
  pixelRatio: number;
}

let atlasPromise: Promise<LoadedAtlas> | null = null;

// Cache-bust query string. Bump whenever the sprite atlas content changes —
// Cloudflare's edge caches /sprites/* with a 4h TTL, so a new atlas otherwise
// only reaches users after the TTL expires (or via a manual cache purge).
// v2: 2026-05-03 — round manhole + square grate catchbasin (D-69 closure).
const SPRITE_VERSION = "v2";

async function fetchAtlas(): Promise<LoadedAtlas> {
  const [json, png] = await Promise.all([
    fetch(`${SPRITE_PATH}@2x.json?${SPRITE_VERSION}`).then((r) => r.json() as Promise<SpriteManifest>),
    fetch(`${SPRITE_PATH}@2x.png?${SPRITE_VERSION}`).then((r) => r.blob()),
  ]);
  const bitmap = await createImageBitmap(png);
  return { manifest: json, bitmap, pixelRatio: 2 };
}

function getAtlas(): Promise<LoadedAtlas> {
  if (!atlasPromise) atlasPromise = fetchAtlas();
  return atlasPromise;
}

export function registerStormSprites(map: MaplibreMap): void {
  const handler = async (e: { id: string }) => {
    const id = e.id;
    if (map.hasImage(id)) return;
    try {
      const atlas = await getAtlas();
      const region = atlas.manifest[id];
      if (!region) return;

      const sub = await createImageBitmap(
        atlas.bitmap,
        region.x,
        region.y,
        region.width,
        region.height,
      );

      if (map.hasImage(id)) return;
      map.addImage(id, sub, { pixelRatio: region.pixelRatio ?? atlas.pixelRatio });
    } catch (err) {
      // Per CLAUDE.md no-silent-fallbacks: surface sprite failures so a
      // future regression doesn't quietly fall back to labels-only.
       
      console.error(`[storm-sprites] Failed to load sprite "${id}":`, err);
    }
  };
  map.on("styleimagemissing", handler);
}
